import React, { useState } from 'react';
import { useTheme } from '../../../ThemeContext';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * FaxViewerPopup - Popup component to display and download fax PDF
 * Styled similar to BillingRestrictionPopup
 * 
 * @param {boolean} visible - Controls popup visibility
 * @param {string} faxUrl - URL of the fax PDF to display
 * @param {Function} onClose - Function to close the popup
 */
const FaxViewerPopup = ({ 
  visible, 
  faxUrl, 
  onClose 
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!visible || !faxUrl) return null;

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch the PDF file
      const response = await fetch(faxUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from URL or use default
      const urlPath = new URL(faxUrl).pathname;
      const fileName = urlPath.split('/').pop() || 'fax-document.pdf';
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading fax:', error);
      // Fallback: Open in new tab if fetch fails (CORS issue in Chrome)
      window.open(faxUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Use Google Docs Viewer to embed the PDF (handles CORS issues in Chrome)
  // This acts as a proxy and works across all browsers
  const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(faxUrl)}&embedded=true`;

  // Theme-based styling (inspired by BillingRestrictionPopup)
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(0.25rem)',
    WebkitBackdropFilter: 'blur(0.25rem)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const popupStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.homePage?.textColor || currentTheme.text?.primary,
    borderRadius: '1rem',
    padding: '0',
    maxWidth: '90vw',
    width: '56.25rem',
    height: '85vh',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 1.25rem 2.5rem rgba(0, 0, 0, 0.6), 0 0 0 0.0625rem rgba(255, 255, 255, 0.1)'
      : '0 1.25rem 2.5rem rgba(0, 0, 0, 0.15), 0 0 0 0.0625rem rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '0.0625rem solid #302d4c' : '0.0625rem solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    display: 'flex',
    flexDirection: 'column'
  };

  const headerStyle = {
    padding: '1.25rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    flexShrink: 0
  };

  const titleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: currentTheme.homePage?.textColor || currentTheme.text?.primary,
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '1.75rem',
    color: currentTheme.homePage?.textColor || currentTheme.text?.primary,
    cursor: 'pointer',
    padding: '0.25rem',
    borderRadius: '0.25rem',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.25rem',
    height: '2.25rem'
  };

  const downloadButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.625rem 1.25rem',
    backgroundColor: '#e63e2b',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const contentStyle = {
    flex: 1,
    overflow: 'hidden',
    padding: '1rem',
    backgroundColor: themeMode === 'dark' ? '#1a1a2e' : '#f5f5f5'
  };

  const iframeContainerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    border: `0.0625rem solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
    boxShadow: themeMode === 'dark' 
      ? '0 0.25rem 0.75rem rgba(0, 0, 0, 0.4)' 
      : '0 0.25rem 0.75rem rgba(0, 0, 0, 0.1)'
  };

  const iframeStyle = {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block'
  };

  // Fax icon SVG
  const FaxIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 9h-1V4H8v5H5c-1.66 0-3 1.34-3 3v7c0 1.1.9 2 2 2h2v-3h12v3h2c1.1 0 2-.9 2-2v-7c0-1.66-1.34-3-3-3zM10 6h6v3h-6V6zm8 12H8v-4h10v4zm2-5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" fill={themeMode === 'dark' ? '#e63e2b' : '#e63e2b'}/>
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-1.25rem) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>
              <FaxIcon />
              Fax Details
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button 
                style={{
                  ...downloadButtonStyle,
                  opacity: isDownloading ? 0.7 : 1,
                  cursor: isDownloading ? 'wait' : 'pointer'
                }}
                onClick={handleDownload}
                disabled={isDownloading}
                onMouseEnter={(e) => !isDownloading && (e.target.style.backgroundColor = '#d32f2f')}
                onMouseLeave={(e) => !isDownloading && (e.target.style.backgroundColor = '#e63e2b')}
              >
                {isDownloading ? (
                  <CircularProgress size={18} style={{ color: '#fff' }} />
                ) : (
                  <DownloadIcon style={{ fontSize: '1.125rem' }} />
                )}
                {isDownloading ? 'Downloading...' : 'Download Fax'}
              </button>
              <button 
                style={closeButtonStyle}
                onClick={onClose}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ×
              </button>
            </div>
          </div>
          
          <div style={contentStyle}>
            <div style={iframeContainerStyle}>
              <iframe
                src={googleDocsViewerUrl}
                style={iframeStyle}
                title="Fax Document"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FaxViewerPopup;
